import type { editor } from 'monaco-editor';
import { useCallback, useEffect, type MutableRefObject } from 'react';
import styled, { useTheme } from 'styled-components';
import { ThemeTypes } from '../../styles/themes/types';
import { MonacoEditor } from '../MonacoEditor/MonacoEditor';
import { Text } from '../Text';

// Imported here for monaco-editor usage specifically
import '@talos/kyoko/src/styles/fonts/codicon';

type MonacoEditor = editor.IStandaloneCodeEditor;

export function hasNonEmptyEditorSelection(monacoEditor: MonacoEditor | null): boolean {
  const selection = monacoEditor?.getSelection();
  return selection != null && !selection.isEmpty();
}

export type JsonMonacoEditorProps = {
  value: string;
  editorRef: MutableRefObject<MonacoEditor | null>;
};

export function JsonMonacoEditor({ value, editorRef }: JsonMonacoEditorProps) {
  const theme = useTheme();
  const monacoTheme = theme.type === ThemeTypes.light ? 'vs' : 'vs-dark';

  const handleEditorDidMount = useCallback(
    (monacoEditor: MonacoEditor) => {
      editorRef.current = monacoEditor;
      monacoEditor.trigger('editor.foldLevel1', 'editor.foldLevel1', null);
    },
    [editorRef]
  );

  useEffect(() => {
    return () => {
      editorRef.current = null;
    };
  }, [editorRef]);

  useEffect(() => {
    const monacoEditor = editorRef.current;
    if (monacoEditor == null) {
      return;
    }

    const currentValue = monacoEditor.getValue();
    if (currentValue !== value) {
      monacoEditor.setValue(value);
      monacoEditor.trigger('editor.foldLevel1', 'editor.foldLevel1', null);
    }
  }, [editorRef, value]);

  return (
    <EditorWrapper>
      <MonacoEditor
        options={{ readOnly: true, minimap: { enabled: false } }}
        height="100%"
        theme={monacoTheme}
        defaultLanguage="json"
        defaultValue={value}
        loading={<Text>Loading editor...</Text>}
        onMount={handleEditorDidMount}
      />
    </EditorWrapper>
  );
}

const EditorWrapper = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
`;
